'use client';

import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { ResponsiveImage } from '../responsiveimage';

interface NavItem {
  name: string;
  href: string;
}

interface NavProps {
  logo: {
    light: string;
    dark: string;
    alt: string;
  };
  desktopItems: NavItem[];
  mobileItems: NavItem[];
  darkTextPages?: string[];
}

export function Navigation({ logo, desktopItems, mobileItems, darkTextPages = [] }: NavProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAboveThreshold, setIsAboveThreshold] = useState(true);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const pathname = usePathname();

  const textColor = isScrolled 
    ? 'text-white' 
    : darkTextPages.some(page => pathname.startsWith(page))
      ? 'text-offblack-300'
      : 'text-white';

  // Determine which logo to show
  const logoSrc = isScrolled 
    ? logo.dark 
    : darkTextPages.some(page => pathname.startsWith(page))
      ? logo.light
      : logo.dark;

  useEffect(() => {
    const threshold = window.innerHeight * 0.55;

    const handleScroll = () => {
      const scrollPosition = window.scrollY;

      if (scrollPosition > 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      if (scrollPosition > threshold) {
        setIsAboveThreshold(true);
      } else {
        setIsAboveThreshold(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled, isAboveThreshold]);

  return (
    <>
      <header 
        className={`w-full z-50 transition-all duration-500 ease-in-out ${
          isScrolled
            ? isAboveThreshold
              ? 'fixed top-0 left-0 right-0 bg-black' // Both true
              : 'absolute top-[-100px] left-0 right-0' // Scrolled but not above threshold
            : isAboveThreshold
              ? 'absolute top-0 left-0 right-0' // Not scrolled but above threshold
              : 'absolute top-0 left-0 right-0 bg-transparent' // Neither scrolled nor above threshold
        }`}
        role="banner"
      >
        <div className={`px-12 flex items-center justify-between transition-all duration-500 ease-in-out ${
          isScrolled ? 'py-3 mt-0' : 'py-4 mt-4'
        }`}>
          {/* Logo */}
          <a 
            href="/" 
            className="relative z-10"
            aria-label="House of Juniper - Return to homepage"
          >
            <ResponsiveImage
              src={logoSrc}
              alt={logo.alt}
              className="w-85 h-auto"
              height={37}
              loading="eager"
              priority
              width={85}
            />
          </a>

          {/* Desktop Navigation */}
          <div className="flex items-center justify-end">
            <nav 
              className="hidden md:flex items-center justify-end flex-1 pr-8"
              role="navigation"
              aria-label="Main navigation"
            >
              <ul className="flex items-center">
                {desktopItems.map((item) => (
                  <li key={item.name} className="ml-8">
                    <a
                      href={item.href}
                      className={`font-display uppercase tracking-widest text-sm hover:text-amber-300 transition-colors duration-300 ${textColor}`}
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Mobile Menu Button */}
            <button
              type="button"
              onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
              className="block relative z-10 text-amber-200 hover:text-white transition-colors duration-300"
              aria-label={isMobileNavOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileNavOpen}
              aria-controls="mobile-navigation"
            >
              <Menu className="w-8 h-8" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      {isMobileNavOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 z-50"
          onClick={() => setIsMobileNavOpen(false)}
          role="presentation"
        />
      )}

      {/* Mobile Navigation Menu */}
      <div 
        id="mobile-navigation"
        className={`fixed top-0 right-0 h-full w-[260px] bg-offblack-300 z-50 transform transition-transform duration-300 ease-in-out ${
          isMobileNavOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
      >
        <div className="h-full flex flex-col pt-12 px-8">
          {/* Close Button */}
          <button
            type="button"
            onClick={() => setIsMobileNavOpen(false)}
            className="absolute top-8 right-8 text-amber-300 hover:text-white transition-colors duration-300"
            aria-label="Close navigation menu"
          >
            <X className="w-6 h-6" aria-hidden="true" />
          </button>
          <a 
            href="/"
            className="absolute top-8 left-8"
            aria-label="House of Juniper - Return to homepage"
          >
            <ResponsiveImage
              src={logo.light}
              alt={logo.alt}
              width={85}
              height={37}
              className="w-85 h-auto"
              priority
            />
          </a>
          <nav 
            role="navigation" 
            aria-label="Mobile navigation"
          >
            <ul className="text-right mt-10">
              {mobileItems.map((item) => (
                <li key={item.name}>
                  <a
                    href={item.href}
                    className="py-3 block font-display uppercase tracking-widest text-white text-sm hover:text-amber-300 transition-colors duration-300"
                    onClick={() => setIsMobileNavOpen(false)}
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
}

// Re-export for backward compatibility
export { Navigation as Nav }; 